import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const t = language === 'en' ? {
    title: "Reset Your Password",
    subtitle: "Enter your new password below.",
    password: "New Password",
    confirmPassword: "Confirm New Password",
    submit: "Reset Password",
    loading: "Resetting...",
    success: "Password Reset!",
    successMsg: "Your password has been reset successfully. You can now sign in with your new password.",
    login: "Sign In",
    mismatch: "Passwords do not match",
    noToken: "Invalid reset link. Please request a new password reset.",
    backLogin: "Back to Sign In",
  } : {
    title: "Réinitialiser Votre Mot de Passe",
    subtitle: "Entrez votre nouveau mot de passe ci-dessous.",
    password: "Nouveau Mot de Passe",
    confirmPassword: "Confirmer le Nouveau Mot de Passe",
    submit: "Réinitialiser",
    loading: "Réinitialisation...",
    success: "Mot de Passe Réinitialisé!",
    successMsg: "Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.",
    login: "Se connecter",
    mismatch: "Les mots de passe ne correspondent pas",
    noToken: "Lien invalide. Veuillez demander une nouvelle réinitialisation.",
    backLogin: "Retour à la Connexion",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError(t.mismatch);
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="bg-[#D4AF37] py-4">
          <div className="max-w-7xl mx-auto px-4">
            <Link to="/"><img src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg" alt="Logo" className="h-12" /></Link>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center py-12 px-4">
          <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-700 mb-4">{t.noToken}</p>
            <Link to="/forgot-password" className="text-[#CD0000] font-bold hover:underline">{t.backLogin}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="bg-[#D4AF37] py-4">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/"><img src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg" alt="Logo" className="h-12" /></Link>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <h1 className="text-2xl font-bold text-[#1D1D1D] mb-2">{t.success}</h1>
              <p className="text-gray-600 text-sm mb-6">{t.successMsg}</p>
              <Link to="/login" data-testid="reset-go-login" className="bg-[#CD0000] text-white px-8 py-3 rounded-lg font-bold inline-flex items-center gap-2 hover:bg-[#c91560] transition-colors">
                {t.login} <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-[#1D1D1D] mb-2">{t.title}</h1>
              <p className="text-gray-600 mb-6 text-sm">{t.subtitle}</p>

              {error && (
                <div data-testid="reset-error" className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {error}
                </div>
              )}

              <form onSubmit={handleSubmit} data-testid="reset-password-form" className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.password}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="reset-password-input"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(''); }}
                      required
                      minLength={6}
                      className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.confirmPassword}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      data-testid="reset-confirm-input"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                      required
                      minLength={6}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="reset-submit-button"
                  disabled={loading}
                  className="w-full bg-[#CD0000] text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:bg-[#c91560] transition-colors disabled:opacity-50"
                >
                  {loading ? t.loading : t.submit} <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
